import type { UIMessage } from "ai";
import { PreviewMessage, ThinkingMessage } from "./message";
import { Greeting } from "./greeting";
import { forwardRef, memo, useEffect, useMemo, useRef, type HTMLAttributes, type MutableRefObject } from "react";
import type { Vote } from "@/lib/db/schema";
import equal from "fast-deep-equal";
import type { UseChatHelpers } from "@ai-sdk/react";
import { useMessages } from "@/hooks/use-messages";
import type { TradingChartRef } from "./TradingChart/TradingChart";
import { Virtuoso, type VirtuosoHandle } from "react-virtuoso";
import {
  clearOutlineTurns,
  deriveOutlineTurns,
  setOutlineJump,
  setOutlineTurns,
} from "./chat-outline/outline-store";

interface MessagesProps {
  chatId: string;
  status: UseChatHelpers["status"];
  votes: Array<Vote> | undefined;
  messages: Array<UIMessage>;
  setMessages: UseChatHelpers["setMessages"];
  reload: UseChatHelpers["reload"];
  isReadonly: boolean;
  chartRef?: MutableRefObject<TradingChartRef | null>;
  append?: UseChatHelpers["append"];
  subAgentMessageIdsRef?: MutableRefObject<Set<string>>;
  messageSkillsRef?: MutableRefObject<Map<string, string[]>>;
  isAgentProcessing?: boolean;
  liveMessagePartsById?: Record<string, any[]>;
  viewMode?: string;
  initialViewMode?: string;
  isAlertsRoute?: boolean;
  alertStartCellIndex?: number | null;
  /** Turn the current chat into a reusable skill — drilled to each assistant
   *  message's action row. Undefined = hidden. */
  onSaveAsSkill?: () => void;
}

function hasAssistantResponseAfterLatestUser(messages: Array<UIMessage>) {
  const latestUserIndex = messages.findLastIndex(
    (message) => message.role === "user"
  );
  if (latestUserIndex === -1) return false;

  return messages.slice(latestUserIndex + 1).some((message) => {
    if (message.role !== "assistant") return false;
    if (typeof message.content === "string" && message.content.trim()) {
      return true;
    }
    return Array.isArray(message.parts) && message.parts.length > 0;
  });
}


function PureMessages({
  chatId,
  status,
  votes,
  messages,
  setMessages,
  reload,
  isReadonly,
  append,
  subAgentMessageIdsRef,
  messageSkillsRef,
  liveMessagePartsById,
  viewMode,
  initialViewMode,
  isAlertsRoute = false,
  alertStartCellIndex,
  onSaveAsSkill,
}: MessagesProps) {
  const {
    containerRef: messagesContainerRef,
    onViewportEnter,
    onViewportLeave,
    hasSentMessage,
  } = useMessages({
    chatId,
    status,
  });

  // Keep latest hook callbacks/ref in stable refs so the Scroller component
  // identity never changes between renders. Defining the Scroller inline (or
  // recreating it each render) makes Virtuoso unmount/remount the scroll
  // container, which causes the flash + jump-back-to-top bug.
  // See https://virtuoso.dev/react-virtuoso/troubleshooting/ and issue #756.
  const containerRefHolder = useRef(messagesContainerRef);
  containerRefHolder.current = messagesContainerRef;
  const onViewportEnterRef = useRef(onViewportEnter);
  onViewportEnterRef.current = onViewportEnter;
  const onViewportLeaveRef = useRef(onViewportLeave);
  onViewportLeaveRef.current = onViewportLeave;

  const Scroller = useMemo(
    () =>
      forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
        ({ onScroll, className, ...props }, ref) => (
          <div
            {...props}
            ref={(node) => {
              containerRefHolder.current.current = node;
              if (typeof ref === "function") ref(node);
              else if (ref) (ref as MutableRefObject<HTMLDivElement | null>).current = node;
            }}
            data-chat-messages
            className={`${className ?? ""} flex-1 overflow-y-auto min-w-0 pt-1 relative`}
            onScroll={(event) => {
              onScroll?.(event);
              const container = event.currentTarget;
              const threshold = 50;
              const isAtBottom =
                container.scrollTop + container.clientHeight >=
                container.scrollHeight - threshold;

              if (isAtBottom) {
                onViewportEnterRef.current();
              } else {
                onViewportLeaveRef.current();
              }
            }}
          />
        )
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  Scroller.displayName = "VirtualizedMessagesScroller";

  // The global "Working" footer should disappear as soon as the assistant has
  // started rendering a response after the latest user message. Tool cards and
  // streamed assistant text are already visible feedback at that point.
  const liveMessagePartsByIdRef = useRef(liveMessagePartsById);
  liveMessagePartsByIdRef.current = liveMessagePartsById;

  // Feed the ChatOutline timeline from the in-memory message list (not the
  // DOM — virtualization unmounts off-screen messages). Register a jump that
  // uses Virtuoso so we can scroll to any turn even when it isn't mounted.
  const virtuosoRef = useRef<VirtuosoHandle | null>(null);
  useEffect(() => {
    setOutlineJump((index) =>
      virtuosoRef.current?.scrollToIndex({
        index,
        align: "center",
        behavior: "smooth",
      }),
    );
    return () => {
      setOutlineJump(null);
      clearOutlineTurns();
    };
  }, []);
  useEffect(() => {
    setOutlineTurns(deriveOutlineTurns(messages));
  }, [messages]);
  const shouldShowWorkingFooter =
    status === "submitted" &&
    messages[messages.length - 1]?.role === "user" &&
    !hasAssistantResponseAfterLatestUser(messages);

  const virtuosoComponents = useMemo(() => {
    const Footer = () => {
      const hasLiveAssistantParts = Object.values(liveMessagePartsByIdRef.current ?? {}).some(
        (parts) => Array.isArray(parts) && parts.length > 0
      );
      return shouldShowWorkingFooter && !hasLiveAssistantParts ? (
        <ThinkingMessage />
      ) : (
        <div className="shrink-0 min-w-[24px] min-h-[24px]" />
      );
    };
    Footer.displayName = "VirtualizedMessagesFooter";
    return { Scroller, Footer };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Scroller, shouldShowWorkingFooter]);

  if (messages.length === 0) {
    return (
      <div
        ref={messagesContainerRef}
        data-chat-messages
        className="flex flex-col min-w-0 pt-1 relative"
      >
        <Greeting />
      </div>
    );
  }

  return (
    <Virtuoso
      ref={virtuosoRef}
      className="flex-1 min-w-0"
      data={messages}
      initialTopMostItemIndex={messages.length - 1}
      followOutput={(isAtBottom) => (isAtBottom ? "smooth" : false)}
      atBottomStateChange={(atBottom) => {
        if (atBottom) onViewportEnter();
        else onViewportLeave();
      }}
      increaseViewportBy={{ top: 800, bottom: 1200 }}
      defaultItemHeight={220}
      itemContent={(index, message) => (
        <div className="pb-3">
          <PreviewMessage
            key={message.id}
            chatId={chatId}
            message={message}
            liveParts={liveMessagePartsById?.[message.id]}
            isLoading={status === "streaming" && messages.length - 1 === index}
            vote={
              votes
                ? votes.find((vote) => vote.messageId === message.id)
                : undefined
            }
            setMessages={setMessages}
            reload={reload}
            isReadonly={isReadonly}
            requiresScrollPadding={
              hasSentMessage && index === messages.length - 1
            }
            append={append}
            isLastMessage={index === messages.length - 1}
            subAgentMessageIdsRef={subAgentMessageIdsRef}
            messageSkillsRef={messageSkillsRef}
            viewMode={viewMode}
            initialViewMode={initialViewMode}
            allMessages={messages}
            isAlertsRoute={isAlertsRoute}
            alertStartCellIndex={alertStartCellIndex}
            onSaveAsSkill={onSaveAsSkill}
          />
        </div>
      )}
      components={virtuosoComponents}
    />
  );
}

export const Messages = memo(PureMessages, (prevProps, nextProps) => {
  if (prevProps.status !== nextProps.status) return false;
  if (prevProps.status && nextProps.status) return false;
  if (prevProps.messages.length !== nextProps.messages.length) return false;
  if (!equal(prevProps.messages, nextProps.messages)) return false;
  if (!equal(prevProps.votes, nextProps.votes)) return false;
  if (prevProps.viewMode !== nextProps.viewMode) return false;
  if (prevProps.initialViewMode !== nextProps.initialViewMode) return false;
  if (prevProps.isAlertsRoute !== nextProps.isAlertsRoute) return false;
  if (prevProps.alertStartCellIndex !== nextProps.alertStartCellIndex) return false;
  return true;
});
